import type { ComponentType, FC, HTMLAttributes, ReactElement, SVGProps } from 'react'

export type IconWeight = 'regular' | 'bold' | 'fill' | 'duotone'

export interface IconProps extends HTMLAttributes<HTMLSpanElement> {
  weight?: IconWeight
}

/**
 * A rendered icon, as consumers pass it: `icon={<Bell />}`.
 *
 * Note that `IconProps` does not constrain the slot: a JSX expression is typed `JSX.Element`,
 * which extends `ReactElement<any, any>`, so any element satisfies this.
 */
export type IconElement = ReactElement<IconProps>

type Variants = Record<IconWeight, ComponentType<SVGProps<SVGSVGElement>>>

/**
 * Factory: takes a 4-weight variant map and returns a React component that renders
 * `<span class="doss-icon"><svg/></span>`. The span is the uniform container; the SVG inside fills
 * it at --icon-size. Applies `doss-icon` (plus any consumer-provided className) to the span and
 * sets `aria-hidden="true"` since icons are decorative — the consuming component is responsible for
 * accessible naming.
 */
export const createIcon = (variants: Variants): FC<IconProps> => {
  const Icon: FC<IconProps> = ({ weight = 'regular', className, ...rest }) => {
    const Component = variants[weight]
    const composed = className !== undefined ? `doss-icon ${className}` : 'doss-icon'
    return (
      <span aria-hidden="true" className={composed} {...rest}>
        <Component />
      </span>
    )
  }
  Icon.displayName = 'Icon'
  return Icon
}
